/**
 * DTOs de "usuarios (self)": `GET /me` y `GET /me/orgs`. Ver docs/FASE-0.md §5 "Usuarios (self)".
 *
 * `MeOrgsResponseSchema`/`MeOrgsEntrySchema`/`MeOrgsModuleSchema` son la forma canónica;
 * `packages/platform-client/src/schemas.ts` las reexporta desde aquí.
 */
import { z } from "zod";
import { NewPasswordSchema } from "./accounts.js";
import { PATTERNS } from "../constants.js";
import { SubscriptionStatusSchema } from "../entitlements.js";
import { OrganizationSchema } from "../webhooks.js";
/** Un módulo, visto desde `/me/orgs`: solo los que tienen suscripción (no cancelada) para esa organización. */
export const MeOrgsModuleSchema = z.object({
    code: z.string().regex(PATTERNS.MODULE_CODE),
    name: z.string(),
    baseUrl: z.url(),
    /** Roles del usuario en ese módulo, dentro de esta organización. */
    roles: z.array(z.string().regex(PATTERNS.KEY)),
    subscriptionStatus: SubscriptionStatusSchema,
});
export const MeOrgsEntrySchema = z.object({
    organization: OrganizationSchema,
    isDefault: z.boolean(),
    modules: z.array(MeOrgsModuleSchema),
    /**
     * Si este usuario administra la organización y puede, por tanto, gestionar sus miembros
     * (ADR-007). Lo calcula el núcleo con la misma regla que autoriza `/orgs/:orgId/members`, para
     * que la interfaz no tenga que deducirla de `modules[].roles` —que además no incluye el módulo
     * del portal, sin suscripción— y no ofrezca una pantalla que después va a dar 403.
     */
    canManageMembers: z.boolean(),
});
/** Respuesta de `GET /api/v1/me/orgs`: alimenta el launcher (organizaciones × módulos del usuario). */
export const MeOrgsResponseSchema = z.object({
    orgs: z.array(MeOrgsEntrySchema),
});
/** Respuesta de `GET /api/v1/me`: perfil mínimo del usuario autenticado. */
export const MeResponseSchema = z.object({
    id: z.uuid(),
    email: z.email(),
    emailVerified: z.boolean(),
    fullName: z.string(),
    status: z.enum(["active", "invited", "suspended"]),
    locale: z.string(),
});
// --- Perfil del propio usuario (docs/FASE-7.md §5 y §11.2) --------------------------------------
/**
 * `PATCH /api/v1/me`. Los dos campos son opcionales, pero el cuerpo vacío se rechaza: una petición
 * que no pide ningún cambio es casi siempre un error del cliente, y aceptarla en silencio lo
 * esconde.
 */
export const UpdateMeInputSchema = z
    .object({
    fullName: z.string().min(1).max(200).optional(),
    /** Etiqueta BCP 47 (`es-PE`, `en`). No se traduce ni se valida contra una lista cerrada. */
    locale: z.string().min(2).max(35).optional(),
})
    .refine((value) => value.fullName !== undefined || value.locale !== undefined, {
    message: "Indica al menos un campo a cambiar.",
});
/**
 * `POST /api/v1/me/password`: cambio con sesión iniciada. Distinto del flujo de olvido
 * (`/account/reset`), que no tiene una sesión de confianza y por eso revoca todas.
 */
export const ChangeMyPasswordInputSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: NewPasswordSchema,
});
/** `POST /api/v1/me/totp/setup`: lo justo para pintar el QR y para quien lo teclee a mano. */
export const MyTotpSetupResponseSchema = z.object({
    otpauthUrl: z.string(),
    /** El mismo secreto del `otpauth://`, para teclearlo si la cámara no coopera. */
    secret: z.string(),
});
export const MyTotpVerifyInputSchema = z.object({
    code: z.string().min(6).max(8),
});
/** `DELETE /api/v1/me/totp`: exige la contraseña. Un token robado no basta para quitar el factor. */
export const DisableMyTotpInputSchema = z.object({
    password: z.string().min(1),
});
/** Estado del segundo factor del propio usuario. */
export const MyTotpStatusSchema = z.object({
    enabled: z.boolean(),
    /** Hay un secreto generado esperando confirmación (`POST /me/totp/verify`). */
    pending: z.boolean(),
});
/**
 * Una sesión abierta del usuario. `userAgent` se devuelve **tal como lo mandó el navegador**: es un
 * dato para que la pantalla lo muestre, nunca algo que el servidor interprete.
 */
export const MySessionSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    /** La sesión desde la que se hizo esta petición (claim `sid` del token). */
    current: z.boolean(),
});
export const MySessionsResponseSchema = z.object({
    sessions: z.array(MySessionSchema),
});
//# sourceMappingURL=me.js.map